import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBell,
  faCircle,
  faCircleNotch,
  faCogs,
  faEllipsisH,
  faEnvelopeOpenText,
  faExclamationTriangle,
  faInfoCircle,
  faScrewdriverWrench,
  faSyncAlt,
  faRetweet,
  faTachometerAlt,
  faTimes,
  faTools,
  faWifi,
} from '@fortawesome/free-solid-svg-icons';
import {
  Alert,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import ReactTooltip from 'react-tooltip';
import css from 'styled-jsx/css';
import { Img } from 'react-image';
import queryString from 'query-string';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';

import AlertService from '../../Services/AlertService';
import AssetGroupService from '../../Services/AssetGroupService';
import AssetService from '../../Services/AssetService';
import AuthService from '../../Services/AuthService';
import DMAlertService from '../../Services/DM1Service';
import TemplateDashboardService from '../../Services/TemplateDashboardService';
import AssetUtils from '../../Lib/AssetUtils';

import {
  ALERTS,
  COMMAND_HISTORY,
  DASHBOARD,
  DETAILS,
  DM1_FAULTS,
  MAINTENANCE as MAINTENANCE_TAB,
  SUBSCRIPTIONS,
  VALIDATION_ERRORS,
  V2_MAINTENANCE,
} from '../../Constants/Asset';
import {
  ASSET_UPDATE,
  CORE,
  MAINTENANCE,
  MAINTENANCE_READ,
  PROVISION,
  PROVISION_UPDATE,
  REMOTE,
  REMOTE_EXECUTE,
  REMOTE_READ,
} from '../../Constants/RBAC';
import {
  CONNECT_DISCONNECT_ID,
  PROVISION_MODEL_ID_SET,
} from '../../Constants/StaticUUIDs';
import {
  XIRGO_V1_VENDOR_ID,
  XIRGO_V2_VENDOR_ID,
  EPEC_6200_VENDOR_ID,
  ACTIA_ACU6_VENDOR_ID,
} from '../../Constants/Vendors';

import CurrentOrgContext from '../../Context/CurrentOrgContext';
import CurrentUserContext from '../../Context/CurrentUserContext';
import IconsContext from '../../Context/IconsContext';
import OrgsMapContext from '../../Context/OrgsMapContext';
import SetCurrentOrgContext from '../../Context/SetCurrentOrgContext';
import { UpdateTagsProvider } from '../../Context/UpdateTagsContext';
import { UpdateCurrentOrgConsumer } from '../../Context/UpdateCurrentOrgContext';
import { SocketContext } from '../../Context/DashboardSocketContext';

import Can from '../../RBAC/Can';
import ActivateAssetModal from './ActivateAssetModal';
import AssetDetailsTabContainer from './AssetDetailsTabContainer';
import AssetDashboardContainer from './AssetDashboardContainer';
import AssetMaintenanceContainer from './AssetMaintenanceContainer';
import AssetAlarmsContainer from './AssetAlarmsTabContainer';
import AssetDM1FaultsContainer from './AssetDM1FaultsContainer';
import AssetSubscriptionsContainer from './AssetSubscriptionsContainer';
import AssetCommandHistoryContainer from './AssetCommandHistoryContainer';
import AssetValidationErrorsContainer from './AssetValidationErrorsContainer';
import AssetV2MaintenanceTabContainer from './AssetV2MaintenanceTabContainer';
import DeleteAssetModal from './DeleteAssetModal';
import EditAssetModal from './EditAssetModal';
import ErrorBoundary from '../ErrorBoundary';
import ExecuteCommandModal from './ExecuteCommandModalContainer';
import GatewaySwapModal from './GatewaySwapModal';
import LoadingCircle from '../LoadingCircle';
import MobileTabLinksModal from './MobileTabLinksModal';
import MoveAssetModal from './MoveAssetModal';
import NewCertificateModal from './NewCertificateModalContainer';
import RerunOutputSignalsModal from './RerunOutputSignalsModal';
import ValidateAssetModal from './ValidateAssetModal';

export default memo(AssetDetailsLayout);

const { className: alertClass, styles: alertStyles } = css.resolve`
  .alert {
    z-index: 1;
    right: 12.5%;
    top: 5rem;
    opacity: 0.9;
  }
`;

let alertTimeout;

const initState = {
  activeAlertCount: 0,
  activeDmAlertCount: 0,
  asset: {},
  assetGroups: [],
  assetLoading: true,
  currentDashboard: {},
  error: '',
  showActivateAssetModal: false,
  showAddUsersModal: false,
  showCreateDashboardModal: false,
  showCreateWidgetModal: false,
  showDeleteDashboardModal: false,
  showEditAssetModal: false,
  showEditDashboardModal: false,
  showGatewaySwapModal: false,
  showRerunOutputSignalsModal: false,
  showValidateAssetModal: false,
  showViewTemplateDashboardsModal: false,
  templateDashboards: [],
  widgets: [],
};

const initAlertState = {
  alertColor: '',
  alertMessage: '',
  alertText: '',
  flexColumn: false,
  showAlert: false,
};

const alertReqParams = {
  transitionFilter: 'positive',
  typeFilter: 'rule',
  severityFilter: 'critical',
};

function AssetDetailsLayout(props) {
  const history = useHistory(),
    { pathname, search } = useLocation(),
    { assetID } = useParams();

  const currentOrg = useContext(CurrentOrgContext) || {};
  const { _id: currentOrgID, features } = currentOrg;
  const currentUser = useContext(CurrentUserContext) || {};
  const orgsMap = useContext(OrgsMapContext);
  const setCurrentOrg = useContext(SetCurrentOrgContext);
  const socket = useContext(SocketContext);

  const alertService = useMemo(() => new AlertService(), []),
    assetGroupService = useMemo(() => new AssetGroupService(), []),
    assetService = useMemo(() => new AssetService(), []),
    assetUtils = useMemo(() => new AssetUtils(), []),
    authService = useMemo(() => new AuthService(), []),
    dmAlertService = useMemo(() => new DMAlertService(), []),
    source = useMemo(() => CancelToken.source(), []),
    templateDashboardService = useMemo(
      () => new TemplateDashboardService(),
      []
    );

  const usesV1Maintenance = useMemo(
      () => features?.maintenance_v1?.enabled ?? false,
      [features?.maintenance_v1?.enabled]
    ),
    usesV2Maintenance = useMemo(
      () => features?.maintenance_v2?.enabled ?? false,
      [features?.maintenance_v2?.enabled]
    ),
    tabLinks = useMemo(() => {
      const staticTabs = [
        [DASHBOARD, 'Dashboard', faTachometerAlt],
        [DETAILS, 'Details', faInfoCircle],
        [ALERTS, 'Alerts', faBell],
        [DM1_FAULTS, 'DM1 Faults', faExclamationTriangle],
        [COMMAND_HISTORY, 'Command History', faWifi],
        [SUBSCRIPTIONS, 'Subscriptions', faEnvelopeOpenText],
        [VALIDATION_ERRORS, 'Errors', faTimes],
      ];
      switch (true) {
        case usesV1Maintenance && usesV2Maintenance:
          staticTabs.splice(
            2,
            0,
            [MAINTENANCE_TAB, 'Maintenance (Legacy)', faTools],
            [V2_MAINTENANCE, 'Maintenance', faScrewdriverWrench]
          );
          break;
        case usesV1Maintenance:
          staticTabs.splice(2, 0, [
            MAINTENANCE_TAB,
            'Maintenance (Legacy)',
            faTools,
          ]);
          break;
        case usesV2Maintenance:
          staticTabs.splice(2, 0, [
            V2_MAINTENANCE,
            'Maintenance',
            faScrewdriverWrench,
          ]);
          break;
      }

      return staticTabs;
    }, [usesV1Maintenance, usesV2Maintenance]);

  const dashboardRef = useRef(null);

  const [state, updateState] = useState(initState);
  const [alertState, updateAlertState] = useState(initAlertState);
  const [commandExecuted, toggleCommandExecuted] = useState(false);
  const [showDeleteAssetModal, toggleDeleteAssetModal] = useState(false);
  const [showExecuteCommandModal, toggleExecuteCommandModal] = useState(false);
  const [showMobileTabLinksModal, toggleMobileTabLinksModal] = useState(false);
  const [showMoveAssetModal, toggleMoveAssetModal] = useState(false);
  const [showNewCertificateModal, toggleNewCertificateModal] = useState(false);

  const isRestrictedAssetForCommands = useMemo(
      () => checkAssetVendorForCommands(state.asset),
      [state.asset]
    ),
    isRestrictedAssetForDM1Tab = useMemo(
      () => checkAssetVendorForDM1Tab(state.asset),
      [state.asset]
    ),
    isRestrictedAssetForOtherThings = useMemo(
      () => checkAssetVendorForOtherThings(state.asset),
      [state.asset]
    ),
    otaHiddenForEdge = useMemo(
      () => checkOtaHiddenForEdge(state.asset),
      [state.asset]
    );

  const { tab = DASHBOARD } = queryString.parse(search);

  const handleSubtractAssetAction = useCallback(
      () => history.push('/organization/assets'),
      [history]
    ),
    handleTabChange = useCallback(
      (t) => {
        history.push(`${pathname}?tab=${t}`);
      },
      [history, pathname]
    );

  const toggleAlert = useCallback(() => {
      updateAlertState(initAlertState);
      updateState((prevState) => ({ ...prevState, error: '' }));
    }, []),
    _alertTimeout = useCallback(() => {
      window.clearTimeout(alertTimeout);
      alertTimeout = window.setTimeout(toggleAlert, 8000);
    }, [toggleAlert]);

  const handleConnectivityUpdate = useCallback(
      ({ signals = {} } = {}) => {
        const { value: connectivity } =
          Object.values(signals || {}).find(
            ({ name }) => name === 'connect-disconnect'
          ) || {};
        if (!connectivity || connectivity === state.connectivity) return;
        updateState((prevState) => ({ ...prevState, connectivity }));
      },
      [state.connectivity]
    ),
    fetchAsset = useCallback(
      async (edit) => {
        try {
          const assetReq = assetService.fetchAsset(
            { assetID, includeLocation: true, shadow: true },
            source.token
          );
          const assetGroupsReq = assetGroupService.fetchGatewayAssetGroups(
            assetID,
            source.token
          );

          const alertCountParams = {
            ...alertReqParams,
            gatewayIdFilter: assetID,
            orgIdFilter: currentOrgID,
            orgID: currentOrgID,
          };
          const activeAlertsCountReq = alertService.fetchAlertByRuleMetrics(
            alertCountParams,
            source.token
          );

          const activeDmAlertsCountReq = dmAlertService.getDM1FaultsCount(
            alertCountParams,
            source.token
          );

          const [
            asset,
            { assetGroups },
            { Count: activeAlertCount },
            { Count: activeDmAlertCount },
          ] = await Promise.all([
            assetReq,
            assetGroupsReq,
            activeAlertsCountReq,
            activeDmAlertsCountReq,
          ]);

          let templateDashboards = [];
          let error = props.socketConnectError;
          if (asset?.gtemplateID) {
            try {
              templateDashboards =
                await templateDashboardService.fetchAllTemplateDashboards(
                  asset.gtemplateID,
                  source.token
                );
            } catch (err) {
              const { message = '' } = err.response?.data ?? err;
              error = message.concat(error ? '\n' : '', error);
            }
          }
          const connectivity = asset
            ? assetUtils.getConnectivityFromShadow(asset)
            : '';

          updateState((prevState) => ({
            ...prevState,
            activeAlertCount,
            activeDmAlertCount,
            asset,
            assetGroups,
            assetLoading: false,
            connectivity,
            currentDashboard: asset.dashboard || {},
            error,
            showActivateAssetModal: false,
            showEditAssetModal: !!edit,
            showValidateAssetModal: false,
            templateDashboards,
            widgets: Array.isArray(asset.gtemplate?.widgets)
              ? asset.gtemplate.widgets
              : [],
          }));

          if (asset.orgID !== currentOrg._id) {
            authService.setCurrentOrgID(asset.orgID);
            setCurrentOrg();
          }
          socket?.on?.(
            `realtime:${assetID}:${CONNECT_DISCONNECT_ID}`,
            handleConnectivityUpdate
          );
          if (edit === false) {
            dashboardRef.current?.fetchWidgetData({
              dashboardID: asset?.dashboard?._id,
              widgets: asset?.gtemplate?.widgets || [],
            });
          }
        } catch (err) {
          if (isCancel(err)) return;
          const { message = '' } = err.response?.data ?? err;
          const error = message.concat(
            props.socketConnectError ? '\n' : '',
            props.socketConnectError
          );
          updateState((prevState) => ({
            ...prevState,
            assetLoading: false,
            error,
          }));
        }
      },
      [
        alertService,
        assetGroupService,
        assetID,
        assetService,
        assetUtils,
        authService,
        currentOrg._id,
        currentOrgID,
        dmAlertService,
        handleConnectivityUpdate,
        props.socketConnectError,
        setCurrentOrg,
        socket,
        source.token,
        templateDashboardService,
      ]
    );

  const cancelGatewaySwap = useCallback(
      async (provisionID) => {
        try {
          await assetService.deleteProvisionRequest(provisionID);

          updateState((prevState) => {
            const asset = { ...prevState.asset, mac: '', provision: null };
            return { ...prevState, asset };
          });
          updateAlertState((prevState) => ({
            ...prevState,
            alertColor: 'cyan-blue',
            alertMessage: 'Gateway Swap Cancelled',
            alertText: 'text-white',
            flexColumn: false,
            showAlert: true,
          }));
          _alertTimeout();
        } catch (err) {
          if (isCancel(err)) return;
          const error = err.response?.data?.message ?? err.message;
          updateState((prevState) => ({ ...prevState, error }));
        }
      },
      [_alertTimeout, assetService]
    ),
    handleActivateAssetSuccess = useCallback(
      async (activatedAsset) => {
        updateState((prevState) => ({ ...prevState, assetLoading: true }));
        await fetchAsset();

        updateAlertState((prevState) => ({
          ...prevState,
          alertColor: 'alert-confirmation',
          alertMessage: `${activatedAsset.nickname} Successfully Activated!`,
          alertText: 'text-white',
          flexColumn: false,
          showAlert: true,
        }));
        _alertTimeout();
      },
      [_alertTimeout, fetchAsset]
    ),
    handleAssetWasMoved = useCallback(
      ({ nickname, orgID } = {}) => {
        const newOrgName = orgsMap[orgID] ? ` to ${orgsMap[orgID].name}` : '';
        const assetMovedMessage = `${
          nickname || 'Asset'
        } successfully moved${newOrgName}`;
        history.push({
          pathname: '/organization/assets',
          state: { assetMovedMessage },
        });
      },
      [history, orgsMap]
    ),
    handleCommandExecutedFromWidget = useCallback(
      (commandName) => {
        updateAlertState((prevState) => ({
          ...prevState,
          alertColor: 'cyan-blue',
          alertMessage: `${commandName} Command Executed`,
          alertText: 'text-white',
          flexColumn: false,
          showAlert: true,
        }));
        _alertTimeout();
      },
      [_alertTimeout]
    ),
    handleGatewaySwap = useCallback((assetUpdate) => {
      updateState((prevState) => ({
        ...prevState,
        asset: { ...prevState.asset, ...assetUpdate },
        showGatewaySwapModal: false,
      }));
    }, []),
    handleOutputSignalRerunSuccess = useCallback(() => {
      updateAlertState((prevState) => ({
        ...prevState,
        alertColor: 'alert-confirmation',
        alertMessage:
          'Success - This action can take several minutes. Check back on the Dashboard for the Calculated Signals',
        alertText: 'text-white',
        flexColumn: false,
        showAlert: true,
      }));
      updateState((prevState) => ({
        ...prevState,
        showRerunOutputSignalsModal: false,
      }));
      _alertTimeout();
    }, [_alertTimeout]),
    handleSubscriptionAlert = useCallback(
      (action) => {
        updateAlertState((prevState) => ({
          ...prevState,
          alertColor: action === 'Added' ? 'cyan-blue' : 'active-dm',
          alertMessage: `Subscription ${action}`,
          alertText: 'text-white',
          flexColumn: false,
          showAlert: true,
        }));
        _alertTimeout();
      },
      [_alertTimeout]
    ),
    handleTagsChange = useCallback((tags) => {
      updateState((prevState) => ({
        ...prevState,
        asset: { ...prevState.asset, tags },
      }));
    }, []),
    handleValidateAssetSuccess = useCallback(async () => {
      updateState((prevState) => ({ ...prevState, assetLoading: true }));
      await fetchAsset();

      updateAlertState((prevState) => ({
        ...prevState,
        alertColor: 'alert-confirmation',
        alertMessage: () => (
          <>
            <p className="mb-0">Validation Process has kicked off</p>
            <p className="mb-0">
              Asset will automatically revert back to Provisioning Model after 4
              hours
            </p>
          </>
        ),
        alertText: 'text-white',
        flexColumn: true,
        showAlert: true,
      }));
      _alertTimeout();
    }, [_alertTimeout, fetchAsset]),
    handleXirgoCertUpdated = useCallback(() => {
      toggleNewCertificateModal(false);
      updateAlertState((prevState) => ({
        ...prevState,
        alertColor: 'cyan-blue',
        alertMessage: 'Certificate Updated',
        alertText: 'text-white',
        flexColumn: false,
        showAlert: true,
      }));
      _alertTimeout();
    }, [_alertTimeout]),
    showCompleteMaintenanceSuccessAlert = useCallback(() => {
      updateAlertState((prevState) => ({
        ...prevState,
        alertColor: 'cyan-blue',
        alertMessage: 'Maintenance Completed Successfully',
        alertText: 'text-white',
        flexColumn: false,
        showAlert: true,
      }));
      _alertTimeout();
    }, [_alertTimeout]),
    showUpdateMaintenanceServiceValuesSuccessAlert = useCallback(() => {
      updateAlertState((prevState) => ({
        ...prevState,
        alertColor: 'cyan-blue',
        alertMessage: 'Maintenance Service Values Updated Successfully',
        alertText: 'text-white',
        flexColumn: false,
        showAlert: true,
      }));
      _alertTimeout();
    }, [_alertTimeout]),
    updateAsset = useCallback((update = {}) => {
      updateState((prevState) => {
        const asset = {
          ...merge(prevState.asset, update),
          settings: update.settings,
        };

        return {
          ...prevState,
          asset,
          showEditAssetModal: false,
        };
      });
    }, []);

  const handleAddUsersSave = useCallback(
      async (users) => {
        const currentDashboard =
          await templateDashboardService.updateTemplateDashboard(
            state.asset?.gtemplateID,
            state.currentDashboard?._id,
            { ...state.currentDashboard, users },
            source.token
          );
        updateState((prevState) => ({
          ...prevState,
          currentDashboard,
          showAddUsersModal: false,
        }));
        updateAlertState((prevState) => ({
          ...prevState,
          alertColor: 'cyan-blue',
          alertMessage: 'Users for Private Dashboard updated',
          alertText: 'text-white',
          flexColumn: false,
          showAlert: true,
        }));
        _alertTimeout();
      },
      [
        _alertTimeout,
        source.token,
        state.asset?.gtemplateID,
        state.currentDashboard,
        templateDashboardService,
      ]
    ),
    handleChangeCurrentDashboard = useCallback((dashboard) => {
      updateState((prevState) => ({
        ...prevState,
        currentDashboard: dashboard || {},
        showViewTemplateDashboardsModal: false,
        widgets: dashboard?.widgets || [],
      }));
      dashboardRef.current?.fetchWidgetData({
        dashboardID: dashboard._id,
        widgets: dashboard.widgets || [],
      });
    }, []),
    handleDashboardCreated = useCallback((dashboard) => {
      updateState((prevState) => {
        const templateDashboards =
          prevState.templateDashboards.concat(dashboard);
        dashboardRef?.current?.fetchWidgetData({
          dashboardID: dashboard?._id,
          widgets: dashboard?.widgets || [],
        });
        return {
          ...prevState,
          currentDashboard: dashboard,
          showCreateDashboardModal: false,
          templateDashboards,
          widgets: dashboard?.widgets || [],
        };
      });
    }, []),
    handleEditDashboardSave = useCallback(
      async ({ isPrivate, isUserPreference, name }) => {
        const updateDashboardRequest =
          templateDashboardService.updateTemplateDashboard(
            state.asset?.gtemplateID,
            state.currentDashboard?._id,
            { name, isPrivate },
            source.token
          );

        let updateUserPreferenceRequest = Promise.resolve();
        if (isUserPreference) {
          updateUserPreferenceRequest =
            templateDashboardService.setUserDefaultDashboard(
              assetID,
              { dashboardID: state.currentDashboard?._id },
              source.token
            );
        }

        await Promise.all([
          updateDashboardRequest,
          updateUserPreferenceRequest,
        ]); // error handled in modal
        updateState((prevState) => ({
          ...prevState,
          currentDashboard: { ...prevState.currentDashboard, isPrivate, name },
          showEditDashboardModal: false,
        }));
        updateAlertState((prevState) => ({
          ...prevState,
          alertColor: 'cyan-blue',
          alertMessage: 'Dashboard updated',
          alertText: 'text-white',
          flexColumn: false,
          showAlert: true,
        }));
        _alertTimeout();
      },
      [
        _alertTimeout,
        assetID,
        source.token,
        state.asset?.gtemplateID,
        state.currentDashboard?._id,
        templateDashboardService,
      ]
    ),
    handleSetDashboardAsDefault = useCallback(async () => {
      try {
        await templateDashboardService.setUserDefaultDashboard(
          state.asset?._id,
          { dashboardID: state.currentDashboard._id },
          source.token
        );
        updateAlertState((prevState) => ({
          ...prevState,
          alertColor: 'cyan-blue',
          alertMessage: 'Dashboard set as default',
          alertText: 'text-white',
          flexColumn: false,
          showAlert: true,
        }));
        _alertTimeout();
      } catch (err) {
        if (isCancel(err)) return;
        const error = err.response?.data?.message ?? err.message;
        updateState((prevState) => ({ ...prevState, error }));
      }
    }, [
      _alertTimeout,
      source.token,
      state.asset?._id,
      state.currentDashboard?._id,
      templateDashboardService,
    ]),
    handleTemplateDashboardDeleted = useCallback((dashboardID) => {
      updateState((prevState) => {
        const templateDashboards = prevState.templateDashboards.filter(
          ({ _id }) => dashboardID !== _id
        );
        const currentDashboard = templateDashboards.find(
          ({ isDefault }) => isDefault
        );
        dashboardRef.current?.fetchWidgetData({
          dashboardID: currentDashboard?._id,
          widgets: currentDashboard?.widgets || [],
        });
        return {
          ...prevState,
          currentDashboard,
          showDeleteDashboardModal: false,
          templateDashboards,
          widgets: currentDashboard?.widgets || [],
        };
      });
    }, []);

  const handleWidgetAdded = useCallback((widgets) => {
      updateState((prevState) => {
        const templateDashboards = prevState.templateDashboards.map((t) => {
          if (t._id !== prevState.currentDashboard._id) return t;
          return { ...t, widgets };
        });

        const currentDashboard = { ...prevState.currentDashboard, widgets };
        dashboardRef?.current?.fetchWidgetData({ widgets });
        return {
          ...prevState,
          currentDashboard,
          showCreateWidgetModal: false,
          templateDashboards,
          widgets,
        };
      });
    }, []),
    handleWidgetDeleted = useCallback((widgetID) => {
      updateState((prevState) => {
        const widgets = prevState.widgets.filter(({ _id }) => widgetID !== _id);
        const currentDashboard = { ...prevState.currentDashboard, widgets };
        dashboardRef?.current?.fetchWidgetData({ widgets });
        return { ...prevState, currentDashboard, widgets };
      });
    }, []),
    handleWidgetUpdated = useCallback((widgets) => {
      updateState((prevState) => {
        const currentDashboard = { ...prevState.currentDashboard, widgets };
        dashboardRef?.current?.fetchWidgetData({ widgets });
        return { ...prevState, currentDashboard, widgets };
      });
    }, []),
    handleWidgetsUpdate = useCallback((widgets) => {
      updateState((prevState) => ({ ...prevState, widgets }));
    }, []);

  const toggleActivateAssetModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showActivateAssetModal: !prevState.showActivateAssetModal,
      }));
    }, []),
    toggleAddUsersModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showAddUsersModal: !prevState.showAddUsersModal,
      }));
    }, []),
    toggleCreateDashboardModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showCreateDashboardModal: !prevState.showCreateDashboardModal,
      }));
    }, []),
    toggleCreateWidgetModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showCreateWidgetModal: !prevState.showCreateWidgetModal,
      }));
    }, []),
    toggleDeleteDashboardModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showDeleteDashboardModal: !prevState.showDeleteDashboardModal,
      }));
    }, []),
    toggleEditDashboardModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showEditDashboardModal: !prevState.showEditDashboardModal,
      }));
    }, []),
    toggleGatewaySwapModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showGatewaySwapModal: !prevState.showGatewaySwapModal,
      }));
    }, []),
    toggleRerunOutputSignalsModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showRerunOutputSignalsModal: !prevState.showRerunOutputSignalsModal,
      }));
    }, []),
    toggleShowEditAssetModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showEditAssetModal: !prevState.showEditAssetModal,
      }));
    }, []),
    toggleValidateAssetModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showValidateAssetModal: !prevState.showValidateAssetModal,
      }));
    }, []),
    toggleViewTemplateDashboardsModal = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        showViewTemplateDashboardsModal:
          !prevState.showViewTemplateDashboardsModal,
      }));
    }, []);

  const updateActiveAlertCount = useCallback((activeAlertCount) => {
    updateState((prevState) => ({ ...prevState, activeAlertCount }));
  }, []);

  useEffect(() => {
    if (!state.error || alertState.showAlert) return;
    updateAlertState((prevState) => ({
      ...prevState,
      alertColor: 'active-dm',
      alertMessage: state.error,
      alertText: 'text-white',
      flexColumn: false,
      showAlert: true,
    }));
    _alertTimeout();
  }, [_alertTimeout, alertState.showAlert, state.error]);

  useEffect(() => {
    const { edit } = queryString.parse(search);
    fetchAsset(edit);
    return () => {
      window.clearTimeout(alertTimeout);
      source.cancel();
    };
  }, []);

  useEffect(() => {
    showMobileTabLinksModal && toggleMobileTabLinksModal(false);
  }, [tab]);

  return (
    <div className="position-absolute h-100 w-100">
      <Alert
        className={`${alertClass} position-fixed w-75 border-0`}
        closeClassName={alertState.alertText}
        color={alertState.alertColor}
        isOpen={alertState.showAlert}
        toggle={toggleAlert}
      >
        <div
          className={`d-flex ${
            alertState.flexColumn ? 'flex-column' : ''
          } align-items-center justify-content-center ${alertState.alertText}`}
        >
          {typeof alertState.alertMessage === 'function'
            ? alertState.alertMessage()
            : alertState.alertMessage}
        </div>
      </Alert>
      <div className="h-100 w-100 d-flex flex-column bg-background-gray pt-2 px-2 p-md-3 py-lg-4 px-lg-5">
        <div className="main-section-container flex-fill">
          <div className="page-info-container d-flex align-items-center">
            <div className="back-button-container mr-3 mr-md-0">
              <Link to="/organization/assets">
                <span className="fa-layers fa-fw three-quarter-text circle">
                  <div className="arrow-state">
                    <FontAwesomeIcon
                      icon={faArrowLeft}
                      size="lg"
                      style={{ left: 6 }}
                      className="text-muted"
                    />
                  </div>

                  <div className="arrow-state-hover">
                    <FontAwesomeIcon icon={faCircle} color="white" size="2x" />
                    <FontAwesomeIcon
                      icon={faArrowLeft}
                      size="lg"
                      style={{ left: 6 }}
                      className="text-muted"
                    />
                  </div>
                </span>
              </Link>
            </div>
            <div className="d-none d-lg-block ml-lg-3">
              <AssetImage
                profileImage={state.asset?.gtemplate?.profileImage ?? ''}
              />
            </div>
            <div>
              <FontAwesomeIcon
                icon={faCircle}
                className={`ml-md-3 text-${
                  state.connectivity === 'deviceConnect'
                    ? 'device-connected-green'
                    : 'icon-link-gray'
                }`}
                size="xs"
              />
            </div>
            <div className="flex-grow-1 ml-2 d-flex align-items-center">
              <h6 className="mb-0 font-weight-bold">
                {state.asset?.nickname || 'Asset Details'}
              </h6>
              {state.asset?.needsNewConfig ? (
                <div
                  className="ml-2"
                  data-for="templateSyncIcon"
                  data-tip="Sync in Progress. Please ensure device is online."
                >
                  <FontAwesomeIcon
                    color="var(--brand-primary)"
                    icon={faSyncAlt}
                  />
                  <ReactTooltip id="templateSyncIcon" />
                </div>
              ) : null}
              {state.asset?.provision ? (
                <div
                  className="ml-2"
                  data-for="assetSwap"
                  data-tip="Waiting for Gateway Swap"
                >
                  <FontAwesomeIcon icon={faRetweet} />
                  <ReactTooltip
                    arrowColor="#fff"
                    className="bg-white rounded-lg shadow border border-icon-link-gray"
                    id="assetSwap"
                    textColor="var(--brand-primary)"
                  />
                </div>
              ) : null}
            </div>

            {isRestrictedAssetForCommands ? null : (
              <div className="execute-button-container d-flex justify-content-end mr-2">
                <Can
                  data={{ assetRemote: state.asset?.remote }}
                  no={() => null}
                  orgID={state.asset?.orgID}
                  perform={REMOTE_EXECUTE}
                  resource={REMOTE}
                  yes={() => (
                    <RestrictedCommandsButton
                      showExecuteCommandModal={showExecuteCommandModal}
                      toggleExecuteCommandModal={toggleExecuteCommandModal}
                    />
                  )}
                />
              </div>
            )}

            <div className="settings-button-container d-flex justify-content-end">
              <Can
                no={(data) => (
                  <RestrictedSettings
                    handleSetDashboardAsDefault={handleSetDashboardAsDefault}
                    isDisabled
                    orgID={state.asset?.orgID}
                    tab={tab}
                    tooltipData={data}
                  />
                )}
                orgID={state.asset?.orgID}
                perform={ASSET_UPDATE}
                resource={CORE}
                yes={() => (
                  <RestrictedSettings
                    cancelGatewaySwap={cancelGatewaySwap}
                    handleSetDashboardAsDefault={handleSetDashboardAsDefault}
                    hasDefaultTemplate={PROVISION_MODEL_ID_SET.has(
                      state.asset?.gtemplateID
                    )}
                    orgID={state.asset?.orgID}
                    provision={state.asset?.provision}
                    showDeleteAssetModal={showDeleteAssetModal}
                    showMoveAssetModal={showMoveAssetModal}
                    showNewCertificateModal={showNewCertificateModal}
                    tab={tab}
                    toggleActivateAssetModal={toggleActivateAssetModal}
                    toggleDeleteAssetModal={toggleDeleteAssetModal}
                    toggleGatewaySwapModal={toggleGatewaySwapModal}
                    toggleMoveAssetModal={toggleMoveAssetModal}
                    toggleNewCertificateModal={toggleNewCertificateModal}
                    toggleRerunOutputSignalsModal={
                      toggleRerunOutputSignalsModal
                    }
                    toggleShowEditAssetModal={toggleShowEditAssetModal}
                    toggleValidateAssetModal={toggleValidateAssetModal}
                    vendor={state.asset?.vendor}
                  />
                )}
              />
            </div>
          </div>
          <div className="page-nav-container d-none d-lg-flex flex-lg-column justify-content-lg-center">
            <ul className="nav">
              {tabLinks
                .filter(([tabName]) => {
                  switch (true) {
                    case state.asset?.format !== 'json' &&
                      tabName === VALIDATION_ERRORS:
                      return false;
                    case isRestrictedAssetForCommands &&
                      [COMMAND_HISTORY].includes(tabName):
                      return false;
                    case isRestrictedAssetForDM1Tab &&
                      [DM1_FAULTS].includes(tabName):
                      return false;
                    default:
                      return true;
                  }
                })
                .map(([tabName, displayName]) => {
                  switch (tabName) {
                    case COMMAND_HISTORY:
                      return (
                        <Can
                          key={tabName}
                          no={() => null}
                          orgID={state.asset?.orgID}
                          perform={REMOTE_READ}
                          resource={REMOTE}
                          yes={() => (
                            <DesktopNavTabLink
                              displayName={displayName}
                              handleTabChange={handleTabChange}
                              isActive={tab === tabName}
                              key={tabName}
                              tabName={tabName}
                            />
                          )}
                        />
                      );
                    case MAINTENANCE_TAB:
                      return (
                        <Can
                          key={tabName}
                          no={() => null}
                          orgID={state.asset?.orgID}
                          perform={MAINTENANCE_READ}
                          resource={MAINTENANCE}
                          yes={() => (
                            <DesktopNavTabLink
                              displayName={displayName}
                              handleTabChange={handleTabChange}
                              isActive={tab === tabName}
                              key={tabName}
                              tabName={tabName}
                            />
                          )}
                        />
                      );
                    default:
                      return (
                        <DesktopNavTabLink
                          activeAlertCount={state.activeAlertCount}
                          activeDmAlertCount={state.activeDmAlertCount}
                          displayName={displayName}
                          handleTabChange={handleTabChange}
                          isActive={tab === tabName}
                          key={tabName}
                          tabName={tabName}
                        />
                      );
                  }
                })}
            </ul>
            <div
              className="w-100 mb-3 bg-third-gray"
              style={{ height: '1px' }}
            />
          </div>
          <div className="main-page-content position-relative">
            {
              {
                [ALERTS]: (
                  <AssetAlarmsContainer
                    {...props}
                    asset={state.asset}
                    updateActiveAlertCount={updateActiveAlertCount}
                  />
                ),
                [COMMAND_HISTORY]: (
                  <AssetCommandHistoryContainer
                    {...props}
                    asset={state.asset}
                    commandExecuted={commandExecuted}
                    toggleCommandExecuted={() =>
                      toggleCommandExecuted(!commandExecuted)
                    }
                  />
                ),
                [DASHBOARD]: (
                  <ErrorBoundary>
                    {state.assetLoading ? (
                      <div className="align-items-center d-flex h-100 justify-content-center w-100">
                        <FontAwesomeIcon
                          color="var(--brand-primary)"
                          icon={faCircleNotch}
                          size="3x"
                          spin
                        />
                      </div>
                    ) : (
                      <AssetDashboardContainer
                        {...state}
                        handleAddUsersSave={handleAddUsersSave}
                        handleChangeCurrentDashboard={
                          handleChangeCurrentDashboard
                        }
                        handleCommandExecutedFromWidget={
                          handleCommandExecutedFromWidget
                        }
                        handleDashboardCreated={handleDashboardCreated}
                        handleEditDashboardSave={handleEditDashboardSave}
                        handleTemplateDashboardDeleted={
                          handleTemplateDashboardDeleted
                        }
                        handleWidgetAdded={handleWidgetAdded}
                        handleWidgetDeleted={handleWidgetDeleted}
                        handleWidgetUpdated={handleWidgetUpdated}
                        handleWidgetsUpdate={handleWidgetsUpdate}
                        ref={dashboardRef}
                        toggleAddUsersModal={toggleAddUsersModal}
                        toggleCreateDashboardModal={toggleCreateDashboardModal}
                        toggleCreateWidgetModal={toggleCreateWidgetModal}
                        toggleDeleteDashboardModal={toggleDeleteDashboardModal}
                        toggleEditDashboardModal={toggleEditDashboardModal}
                        toggleViewTemplateDashboardsModal={
                          toggleViewTemplateDashboardsModal
                        }
                      />
                    )}
                  </ErrorBoundary>
                ),
                [DETAILS]: (
                  <AssetDetailsTabContainer
                    {...props}
                    asset={state.asset}
                    assetGroups={state.assetGroups}
                    currentUser={currentUser}
                    isRestrictedAssetForOtherThings={
                      isRestrictedAssetForOtherThings
                    }
                    otaHiddenForEdge={otaHiddenForEdge}
                  />
                ),
                [DM1_FAULTS]: (
                  <AssetDM1FaultsContainer {...props} asset={state.asset} />
                ),
                [MAINTENANCE]: (
                  <AssetMaintenanceContainer asset={state.asset} />
                ),
                [SUBSCRIPTIONS]: !isEmpty(state.asset) ? (
                  <AssetSubscriptionsContainer
                    {...props}
                    asset={state.asset}
                    handleSubscriptionAlert={handleSubscriptionAlert}
                  />
                ) : null,
                [VALIDATION_ERRORS]: (
                  <AssetValidationErrorsContainer
                    {...props}
                    asset={state.asset}
                  />
                ),
                [V2_MAINTENANCE]: state.asset?._id ? (
                  <AssetV2MaintenanceTabContainer
                    {...props}
                    {...state}
                    showCompleteMaintenanceSuccessAlert={
                      showCompleteMaintenanceSuccessAlert
                    }
                    showUpdateMaintenanceServiceValuesSuccessAlert={
                      showUpdateMaintenanceServiceValuesSuccessAlert
                    }
                  />
                ) : (
                  <LoadingCircle size="3x" />
                ),
              }[tab]
            }
          </div>
        </div>
        <div className="d-flex justify-content-around align-items-center d-lg-none bg-white shadow-lg mx-n2 py-1">
          {tabLinks.slice(0, 4).map(([tabName, displayName, icon]) =>
            tabName === MAINTENANCE_TAB ? (
              <Can
                key={`${tabName}:${displayName}`}
                no={() => null}
                orgID={state.asset?.orgID}
                perform={MAINTENANCE_READ}
                resource={MAINTENANCE}
                yes={() => (
                  <button
                    key={`${tabName}:${displayName}`}
                    className="position-relative btn btn-link d-flex flex-column px-0 align-items-center three-quarter-text text-decoration-none"
                    onClick={() => handleTabChange(tabName)}
                    style={{
                      color: `var(--${
                        tab === tabName && !showMobileTabLinksModal
                          ? 'brand-primary'
                          : 'text-lighter'
                      })`,
                    }}
                  >
                    <FontAwesomeIcon icon={icon} />
                    {displayName}
                  </button>
                )}
              />
            ) : (
              <button
                key={`${tabName}:${displayName}`}
                className="position-relative btn btn-link d-flex flex-column px-0 align-items-center three-quarter-text text-decoration-none"
                onClick={() => handleTabChange(tabName)}
                style={{
                  color: `var(--${
                    tab === tabName && !showMobileTabLinksModal
                      ? 'brand-primary'
                      : 'text-lighter'
                  })`,
                }}
              >
                <FontAwesomeIcon icon={icon} />
                {displayName}
                <ActiveAlertBadge
                  activeAlertCount={state.activeAlertCount}
                  activeDmAlertCount={state.activeDmAlertCount}
                  mobile
                  tab={tabName}
                />
              </button>
            )
          )}
          <button
            className="btn btn-link d-flex flex-column px-0 align-items-center three-quarter-text text-decoration-none"
            onClick={() => toggleMobileTabLinksModal(!showMobileTabLinksModal)}
            style={{
              color: `var(--${
                showMobileTabLinksModal ? 'brand-primary' : 'text-lighter'
              })`,
            }}
          >
            <FontAwesomeIcon icon={faEllipsisH} />
            More
          </button>
        </div>
      </div>
      {state.showActivateAssetModal ? (
        <ActivateAssetModal
          asset={state.asset}
          handleActivateAssetSuccess={handleActivateAssetSuccess}
          isOpen={state.showActivateAssetModal}
          toggleModal={toggleActivateAssetModal}
        />
      ) : null}
      {showExecuteCommandModal ? (
        <ExecuteCommandModal
          asset={state.asset}
          isOpen={showExecuteCommandModal}
          toggleCommandExecuted={() => toggleCommandExecuted(!commandExecuted)}
          toggleModal={() =>
            toggleExecuteCommandModal(!showExecuteCommandModal)
          }
        />
      ) : null}
      {showMoveAssetModal ? (
        <UpdateCurrentOrgConsumer>
          {(updateCurrentOrg) => (
            <MoveAssetModal
              currentOrg={currentOrg}
              isOpen={showMoveAssetModal}
              handleAssetWasMoved={handleAssetWasMoved}
              selectedAsset={state.asset}
              toggleModal={() => toggleMoveAssetModal(!showMoveAssetModal)}
              updateCurrentOrg={updateCurrentOrg}
            />
          )}
        </UpdateCurrentOrgConsumer>
      ) : null}
      {state.showEditAssetModal ? (
        <UpdateTagsProvider value={handleTagsChange}>
          <EditAssetModal
            asset={state.asset}
            isOpen={state.showEditAssetModal}
            refreshAsset={fetchAsset}
            toggle={toggleShowEditAssetModal}
            updateAsset={updateAsset}
          />
        </UpdateTagsProvider>
      ) : null}
      {showDeleteAssetModal ? (
        <DeleteAssetModal
          isOpen={showDeleteAssetModal}
          toggleModal={() => toggleDeleteAssetModal(!showDeleteAssetModal)}
          assetToDelete={state.asset}
          handleSubtractAsset={handleSubtractAssetAction}
        />
      ) : null}
      {showNewCertificateModal ? (
        <NewCertificateModal
          asset={state.asset}
          handleXirgoCertUpdated={handleXirgoCertUpdated}
          isOpen={showNewCertificateModal}
          toggleModal={() =>
            toggleNewCertificateModal(!showNewCertificateModal)
          }
        />
      ) : null}
      {showMobileTabLinksModal ? (
        <MobileTabLinksModal
          assetFormat={state.asset?.format}
          handleTabChange={handleTabChange}
          isOpen={showMobileTabLinksModal}
          tab={tab}
          toggleModal={() =>
            toggleMobileTabLinksModal(!showMobileTabLinksModal)
          }
        />
      ) : null}
      {state.showRerunOutputSignalsModal ? (
        <RerunOutputSignalsModal
          handleOutputSignalRerunSuccess={handleOutputSignalRerunSuccess}
          isOpen={state.showRerunOutputSignalsModal}
          toggleModal={toggleRerunOutputSignalsModal}
        />
      ) : null}
      {state.showGatewaySwapModal ? (
        <GatewaySwapModal
          asset={state.asset}
          handleGatewaySwap={handleGatewaySwap}
          isOpen={state.showGatewaySwapModal}
          toggleModal={toggleGatewaySwapModal}
        />
      ) : null}
      {state.showValidateAssetModal ? (
        <ValidateAssetModal
          asset={state.asset}
          handleValidateAssetSuccess={handleValidateAssetSuccess}
          isOpen={state.showValidateAssetModal}
          toggleModal={toggleValidateAssetModal}
        />
      ) : null}
      {alertStyles}
      <style jsx>{`
        .main-section-container {
          display: grid;
          grid-template-columns: 1fr;
          grid-template-rows: 35px auto;
          grid-template-areas: 'page-info' 'main-page-content';
        }
        .page-info-container {
          grid-area: page-info;
        }
        .main-page-content {
          grid-area: main-page-content;
        }
        .back-button-container {
          grid-area: back-button;
          align-self: center;
        }
        .btn:focus {
          box-shadow: none;
        }
        @media (min-width: 992px) {
          .main-section-container {
            grid-template-rows: 30px 60px minmax(auto, auto);
            grid-template-areas:
              'desktop-page-info'
              'desktop-tab-nav'
              'desktop-main-content';
          }
          .page-info-container {
            grid-area: desktop-page-info;
          }
          .page-nav-container {
            grid-area: desktop-tab-nav;
          }
          .main-page-content {
            grid-area: desktop-main-content;
          }
        }
      `}</style>
    </div>
  );
}

AssetDetailsLayout.propTypes = { socketConnectError: PropTypes.string };

// display components

function AssetImage({ profileImage }) {
  const { missingImage } = useContext(IconsContext);
  return (
    <Img
      className="img-fluid mh-100"
      container={(children) => <div style={{ maxWidth: 30 }}>{children}</div>}
      src={[profileImage, missingImage]}
    />
  );
}

AssetImage.propTypes = { profileImage: PropTypes.string };

function DesktopNavTabLink(props) {
  return (
    <li
      key={props.tabName}
      className={`${props.isActive ? 'active-tab' : 'inactive-tab'} nav-item`}
    >
      <button
        className="btn btn-link three-quarter-text text-decoration-none"
        onClick={() => props.handleTabChange(props.tabName)}
      >
        {props.displayName}
        <ActiveAlertBadge
          activeAlertCount={props.activeAlertCount}
          activeDmAlertCount={props.activeDmAlertCount}
          tab={props.tabName}
        />
      </button>
      <style jsx>{`
        .active-tab {
          border-bottom: 3px solid var(--brand-primary);
        }
        .active-tab button {
          color: var(--text-muted);
          font-size: 0.8rem !important;
          font-weight: 500 !important;
        }
        .btn:focus {
          box-shadow: none;
        }
        .inactive-tab button {
          font-weight: 400;
          color: var(--muted-gray);
          font-size 0.8rem;
        }
        .inactive-tab button:hover {
          color: var(--text-muted);
        }
      `}</style>
    </li>
  );
}

DesktopNavTabLink.propTypes = {
  activeAlertCount: PropTypes.number,
  activeDmAlertCount: PropTypes.number,
  displayName: PropTypes.string,
  handleTabChange: PropTypes.func,
  isActive: PropTypes.bool,
  tabName: PropTypes.string,
};

function RestrictedCommandsButton({
  isDisabled,
  showExecuteCommandModal,
  toggleExecuteCommandModal,
}) {
  const conditionallyDisabled = isDisabled ? { disabled: true } : {};
  return (
    <button
      {...conditionallyDisabled}
      className="btn modal-button-primary three-quarter-text p-1 py-md-0 px-md-4"
      onClick={() => toggleExecuteCommandModal(!showExecuteCommandModal)}
    >
      <span className="text-reset d-none d-md-inline">Execute</span> Command
    </button>
  );
}

RestrictedCommandsButton.propTypes = {
  isDisabled: PropTypes.bool,
  showExecuteCommandModal: PropTypes.bool,
  toggleExecuteCommandModal: PropTypes.func,
};

function RestrictedSettings(props) {
  const { className: settingsClass, styles: settingsStyles } = css.resolve`
    button:hover {
      color: white !important;
      background-color: #6c757d !important;
    }
  `;
  const { className: dropdownClass, styles: dropdownStyles } = css.resolve`
    .dropdown-menu {
      border-radius: 1rem;
      z-index: 1050;
    }
  `;
  const { className: dropdownItemClass, styles: dropdownItemStyles } =
    css.resolve`
      button {
        font-size: 0.8rem;
        cursor: pointer;
        box-shadow: none;
        outline: none;
        color: var(--text-muted);
      }
      button:hover {
        background-color: transparent;
      }
    `;
  const conditionallyDisabled = props.isDisabled ? { disabled: true } : {};
  const conditionalTooltip = props.isDisabled
    ? { ['data-for']: 'assetSettings', ['data-tip']: props.tooltipData }
    : {};
  return (
    <UncontrolledDropdown>
      <DropdownToggle
        {...conditionalTooltip}
        color="outline-secondary"
        className={`p-1 py-md-0 px-md-4 ${settingsClass} bg-white three-quarter-text shadow-none`}
      >
        <FontAwesomeIcon icon={faCogs} className="d-md-none" />
        <span className="d-none d-md-inline">Settings</span>
        <ReactTooltip id="assetSettings" />
      </DropdownToggle>
      <DropdownMenu right className={`${dropdownClass} shadow`}>
        {!props.isDisabled && props.hasDefaultTemplate ? (
          <DropdownItem
            className={dropdownItemClass}
            onClick={props.toggleActivateAssetModal}
          >
            Activate Asset
          </DropdownItem>
        ) : null}
        <DropdownItem
          {...conditionallyDisabled}
          className={dropdownItemClass}
          onClick={() => props.toggleMoveAssetModal(!props.showMoveAssetModal)}
        >
          Move
        </DropdownItem>
        <DropdownItem
          {...conditionallyDisabled}
          className={dropdownItemClass}
          onClick={props.toggleShowEditAssetModal}
        >
          Edit
        </DropdownItem>
        {!['1'].includes(props.vendor) ? (
          <Can
            no={() => null}
            orgID={props.orgID}
            perform={PROVISION_UPDATE}
            resource={PROVISION}
            yes={() => (
              <DropdownItem
                className={dropdownItemClass}
                onClick={() =>
                  props.toggleNewCertificateModal(
                    !props.showNewCertificateModal
                  )
                }
              >
                New Certificate
              </DropdownItem>
            )}
          />
        ) : null}
        <DropdownItem
          className={props.tab !== DASHBOARD ? 'd-none' : dropdownItemClass}
          onClick={props.handleSetDashboardAsDefault}
        >
          Set Dashboard as Default
        </DropdownItem>
        {!props.isDisabled && props.hasDefaultTemplate ? (
          <DropdownItem
            className={dropdownItemClass}
            onClick={props.toggleValidateAssetModal}
          >
            Validate Asset
          </DropdownItem>
        ) : null}
        <Can
          no={() => null}
          orgID={props.orgID}
          perform={PROVISION_UPDATE}
          resource={PROVISION}
          yes={() => (
            <DropdownItem
              className={
                props.vendor === 'elevat' ? dropdownItemClass : 'd-none'
              }
              onClick={() =>
                props.provision
                  ? props.cancelGatewaySwap(props.provision._id)
                  : props.toggleGatewaySwapModal()
              }
            >
              {props.provision ? 'Cancel Gateway Swap' : 'Swap Gateway'}
            </DropdownItem>
          )}
        />
        <DropdownItem
          {...conditionallyDisabled}
          className={dropdownItemClass}
          onClick={props.toggleRerunOutputSignalsModal}
        >
          Rerun Calculated Signals
        </DropdownItem>
        <DropdownItem
          {...conditionallyDisabled}
          className={`${dropdownItemClass} text-danger`}
          onClick={() =>
            props.toggleDeleteAssetModal(!props.showDeleteAssetModal)
          }
        >
          Delete
        </DropdownItem>
      </DropdownMenu>
      {dropdownItemStyles}
      {dropdownStyles}
      {settingsStyles}
    </UncontrolledDropdown>
  );
}

RestrictedSettings.propTypes = {
  cancelGatewaySwap: PropTypes.func,
  handleSetDashboardAsDefault: PropTypes.func,
  hasDefaultTemplate: PropTypes.bool,
  isDisabled: PropTypes.bool,
  orgID: PropTypes.string,
  provision: PropTypes.shape({ _id: PropTypes.string }),
  showDeleteAssetModal: PropTypes.bool,
  showMoveAssetModal: PropTypes.bool,
  showNewCertificateModal: PropTypes.bool,
  tab: PropTypes.string,
  toggleActivateAssetModal: PropTypes.func,
  toggleDeleteAssetModal: PropTypes.func,
  toggleGatewaySwapModal: PropTypes.func,
  toggleMoveAssetModal: PropTypes.func,
  toggleNewCertificateModal: PropTypes.func,
  toggleRerunOutputSignalsModal: PropTypes.func,
  toggleShowEditAssetModal: PropTypes.func,
  toggleValidateAssetModal: PropTypes.func,
  tooltipData: PropTypes.string,
  vendor: PropTypes.oneOf([
    '1',
    'elevat',
    XIRGO_V1_VENDOR_ID,
    XIRGO_V2_VENDOR_ID,
    EPEC_6200_VENDOR_ID,
    ACTIA_ACU6_VENDOR_ID,
  ]),
};

function ActiveAlertBadge({
  activeAlertCount,
  activeDmAlertCount,
  mobile,
  tab,
}) {
  const mobileStyle = mobile ? { right: '-0.25rem' } : {};
  switch (true) {
    case ![ALERTS, DM1_FAULTS].includes(tab):
      return null;
    case tab === ALERTS && !activeAlertCount:
    case tab === DM1_FAULTS && !activeDmAlertCount:
      return null;
    default:
      return (
        <span
          className={`badge badge-danger ml-1 ${
            mobile ? 'position-absolute' : ''
          }`}
          style={mobileStyle}
        >
          {tab === ALERTS ? activeAlertCount : activeDmAlertCount}
        </span>
      );
  }
}

ActiveAlertBadge.propTypes = {
  activeAlertCount: PropTypes.number,
  activeDmAlertCount: PropTypes.number,
  mobile: PropTypes.bool,
  tab: PropTypes.string,
};

function checkAssetVendorForCommands(asset = {}) {
  if (!Object.keys(asset).length) return true;

  return ['1'].includes(asset?.vendor);
}

function checkAssetVendorForOtherThings(asset = {}) {
  if (!Object.keys(asset).length) return true;

  return ['1', XIRGO_V1_VENDOR_ID, XIRGO_V2_VENDOR_ID].includes(asset?.vendor);
}

function checkOtaHiddenForEdge(asset = {}) {
  if (!Object.keys(asset).length) return true;

  return [EPEC_6200_VENDOR_ID, ACTIA_ACU6_VENDOR_ID].includes(asset?.vendor);
}

function checkAssetVendorForDM1Tab(asset = {}) {
  if (!Object.keys(asset).length) return true;

  return ['1', XIRGO_V1_VENDOR_ID].includes(asset?.vendor); // xirgo V2 now has dm1
}
